import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

/* ---------------- the agent avatar ---------------- */

// Frames of the same character, all lined up on the body. They live on the server
// (/var/www/fitamingo-media/agent) so the character can be updated without a release.
const FRAME_URL = "/media/agent/";
const FRAME_NAMES = ["base", "blink", "talk1", "talk2", "lift1", "lift2"];

/**
 * The assistant in the corner, with no background.
 * It breathes and blinks (mouth never moves, Ali's choice); a finished task gets a small hop.
 * Left alone for a while, it grabs two dumbbells and does a few (very serious) curls.
 */
export default function AgentAvatar({ phase, levelsRef, size = 84, hop = 0, workoutKey = 0, autoWorkout = true }) {
  const bodyRef = useRef(null);
  const shadowRef = useRef(null);
  const frameRefs = useRef({});
  const hopAt = useRef(0);

  useEffect(() => {
    if (hop) hopAt.current = performance.now();
  }, [hop]);

  // workoutKey changes => do a workout now (used by the homepage).
  const forceWorkout = useRef(false);
  useEffect(() => {
    if (workoutKey) forceWorkout.current = true;
  }, [workoutKey]);

  useEffect(() => {
    let raf = 0;
    let nextBlink = performance.now() + 1800;
    let blinkUntil = 0;
    let shown = "";
    // Workout plan: only while idle. First one ~12s after load, then every 30-50s.
    let nextWorkout = phase === "idle" && autoWorkout ? performance.now() + 12000 : Infinity;
    let workoutStart = 0;
    const WORKOUT = 6200; // ms
    const show = (name) => {
      if (name === shown) return;
      FRAME_NAMES.forEach((n) => {
        const el = frameRefs.current[n];
        if (el) el.style.opacity = n === name ? "1" : "0";
      });
      shown = name;
    };
    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      const t = now / 1000;
      let frame = "base";
      let y = 0;
      let sy = 1 + Math.sin(t * (phase === "idle" ? 1.9 : 2.3)) * 0.018;
      let sx = 1 - Math.sin(t * (phase === "idle" ? 1.9 : 2.3)) * 0.01;
      let r = 0;

      if (phase === "idle" && !workoutStart && (now > nextWorkout || forceWorkout.current)) {
        workoutStart = now;
        forceWorkout.current = false;
      }
      if (phase !== "idle") workoutStart = 0;

      if (workoutStart) {
        // Pick up the dumbbells, 4 reps, a wobbly last rep, put them down.
        const k = now - workoutStart;
        if (k < 700) frame = "lift1";
        else if (k < 700 + 4 * 900) {
          const rep = (k - 700) % 900;
          frame = rep < 450 ? "lift2" : "lift1";
          if (frame === "lift2") {
            sy *= 0.97; // squashed by the weight
            sx *= 1.02;
          }
        } else if (k < WORKOUT - 600) {
          frame = "lift2"; // the hardest rep: trembling
          r = Math.sin(t * 38) * 1.4;
          y = Math.sin(t * 31) * 0.6;
        } else if (k < WORKOUT) frame = "lift1";
        else {
          workoutStart = 0;
          nextWorkout = autoWorkout ? now + 30000 + Math.random() * 20000 : Infinity;
        }
      } else {
        if (now > nextBlink) {
          blinkUntil = now + 140;
          nextBlink = now + 2400 + Math.random() * 3200;
        }
        if (now < blinkUntil) frame = "blink";
      }
      show(frame);

      // A small, soft hop when a task finishes.
      const since = now - hopAt.current;
      if (since < 450) {
        const k = since / 450;
        y -= Math.sin(k * Math.PI) * 6;
        sy *= 1 + Math.sin(k * Math.PI) * 0.02;
      }
      if (bodyRef.current) bodyRef.current.style.transform = `translateY(${y.toFixed(2)}px) rotate(${r.toFixed(2)}deg) scale(${sx.toFixed(3)}, ${sy.toFixed(3)})`;
      if (shadowRef.current) shadowRef.current.style.transform = `scale(${(1 + y / 60).toFixed(3)})`;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase, levelsRef, autoWorkout]);

  const glow =
    phase === "agent" ? "bg-lemon/45" : phase === "user" ? "bg-white/70" : phase === "working" || phase === "thinking" ? "bg-lemon/25" : "bg-transparent";

  return (
    <span className="relative block" style={{ width: size, height: size }}>
      <span aria-hidden="true" className={cn("absolute inset-[16%] rounded-full blur-xl transition-colors duration-300", glow)} />
      <span ref={shadowRef} aria-hidden="true" className="absolute bottom-[7%] left-[27%] h-[7%] w-[46%] rounded-[50%] bg-black/45 blur-[3px]" />
      <span ref={bodyRef} className="absolute inset-0 block origin-bottom will-change-transform">
        {FRAME_NAMES.map((n) => (
          <img
            key={n}
            ref={(el) => {
              frameRefs.current[n] = el;
            }}
            src={`${FRAME_URL}${n}.webp`}
            alt=""
            draggable={false}
            decoding="async"
            className="absolute inset-0 h-full w-full select-none"
            style={{ opacity: n === "base" ? 1 : 0 }}
          />
        ))}
      </span>
    </span>
  );
}
